import { useState } from 'react';
import type React from 'react';
import type { HTMLAttributes } from 'react';

import type { RenderModelPage } from '@aline/common';
import { AlineTransforms, SelEventSource } from '@aline/common';
import type { NodeEntry } from 'slate';
import { Range } from 'slate';
import type { ContextMenuLocation } from 'src/editor';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { openImageDialogWithImage } from 'src/editor/open-image-dialog';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import type { MouseDownSelectOptions } from 'src/editor/transforms/selection-transforms';
import { useCurrentUserId } from 'src/gql';
import { getEffectiveZoom } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import { useGetEditorUserState } from 'src/hooks';
import { useEditingDisabled } from 'src/store/document/selectors';

import { useEditor, useHeaderOrFooterOpen } from '../hooks';

export function usePageEventHandlers(options: {
  pageEl: HTMLDivElement | null;
  rMPageEntry: NodeEntry<RenderModelPage>;
}) {
  const {
    pageEl,
    rMPageEntry: [page, path],
  } = options;
  const editor = useEditor();

  const authorId = useCurrentUserId();
  const editorTabStateActions = useEditorTabStateActions();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const { isInSigningCeremony, isReadOnly } = useGetEditorUserState();
  /**
   * Combine `useEditingDisabled()` with the direct workflow flags so
   * drag-image / drop-image / skipFocus respect signing and read-only state
   * during the brief window before AlineEditor flips editingMode to VIEWING.
   */
  const editingDisabled = useEditingDisabled() || isInSigningCeremony || isReadOnly;
  /**
   * Mobile relies on tap-to-select without surfacing the soft keyboard when
   * editing is disabled (signing/read-only/viewing); skipping focus prevents
   * an auto-keyboard popup on iOS/Android.
   */
  const skipFocus = IS_MOBILE && editingDisabled;
  const isHeaderOpen = useHeaderOrFooterOpen('header');
  const isFooterOpen = useHeaderOrFooterOpen('footer');
  const [contextMenuLocation, setContextMenuLocation] = useState<ContextMenuLocation>(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);

  function getPointerPosition(
    event: React.MouseEvent
  ): { pointerX: number; pointerY: number } | null {
    if (!pageEl) return null;
    const { clientX, clientY } = event;
    const { left, top } = pageEl.getBoundingClientRect();
    /**
     * Mobile fit-to-screen applies `transform: scale` to the editor container,
     * so getBoundingClientRect returns post-transform pixels but the render
     * model expects layout-space coordinates. Divide by the effective zoom so
     * pointer-to-render-model resolution works at any scale.
     */
    const zoom = getEffectiveZoom(pageEl);
    return { pointerX: (clientX - left) / zoom, pointerY: (clientY - top) / zoom };
  }

  function handleContextMenu(event: React.MouseEvent) {
    /**
     * Allow browser's default context menu when CMD/Meta key is held
     * down while right-clicking on the page. This is helpful for
     * debugging as well as being a nice escape hatch for users.
     */
    if (event.metaKey) return;

    const pointerPosition = getPointerPosition(event);
    if (!pointerPosition) return;

    event.preventDefault();
    event.stopPropagation();

    const clickedObject = Queries.resolvePointerToClickable(editor, {
      isFooterOpen,
      isHeaderOpen,
      rMElementEntry: [page, path],
      ...pointerPosition,
    });

    // If the right click is within a list marker, select the list marker to show the list options in the context menu.
    if (clickedObject && clickedObject.type === 'list-marker') {
      AlineTransforms.selectListMarker(
        editor,
        Queries.getCMPathFromRMPath(editor, clickedObject.rMParagraphEntry[1])
      );
    }

    setContextMenuLocation(
      contextMenuLocation === null ? { left: event.clientX, top: event.clientY } : null
    );
  }

  function handleMouseDown(
    event: React.MouseEvent,
    options: Pick<
      MouseDownSelectOptions,
      'dispatchClick' | 'forceAllowHeaderAndFooter' | 'skipScrollSelectionIntoView'
    > = {}
  ) {
    if (!authorId || !editingMode) return;

    const pointerPosition = getPointerPosition(event);
    if (!pointerPosition) return;

    event.preventDefault();

    const isRightClick = event.button === 2;
    const isSelectionExpanded = editor.selection && Range.isExpanded(editor.selection);
    const isNoOpRightClick = isRightClick && isSelectionExpanded;
    if (isNoOpRightClick) return;

    ClientTransforms.mouseDownSelect(editor, {
      authorId,
      editingMode,
      isShift: event.shiftKey,
      rMPageEntry: [page, path],
      skipFocus,
      ...pointerPosition,
      ...options,
    });
  }

  function handleMouseMove(event: React.MouseEvent) {
    const pointerPosition = getPointerPosition(event);
    if (!pointerPosition) return;

    event.preventDefault();

    ClientTransforms.mouseMoveSelect(editor, {
      rMPageEntry: [page, path],
      ...pointerPosition,
    });
  }

  function handleDragOver(event: React.DragEvent) {
    if (editingDisabled) return;

    const hasImage = Array.from(event.dataTransfer.items).some(item =>
      item.type.startsWith('image/')
    );
    if (!hasImage) return;

    event.preventDefault();

    const pointerPosition = getPointerPosition(event);
    if (!pointerPosition) return;

    const pointDetails = Queries.resolvePointerToPoint(editor, {
      ...pointerPosition,
      isFooterOpen,
      isHeaderOpen,
      rMElementEntry: [page, path],
    });
    if (!pointDetails) return editorTabStateActions.dragDrop.setMousePageCoords(null);
    const { affinity, cMPoint: point } = pointDetails;

    setIsDraggingImage(true);
    editorTabStateActions.dragDrop.setMousePageCoords({ ...point, affinity });
  }

  function handleDrop(event: React.DragEvent) {
    if (editingDisabled) return;

    const image = Array.from(event.dataTransfer.files).find(file => file.type.startsWith('image/'));
    if (!image) return;

    event.preventDefault();

    const pointerPosition = getPointerPosition(event);
    if (!pointerPosition) return;

    const pointDetails = Queries.resolvePointerToPoint(editor, {
      ...pointerPosition,
      isFooterOpen,
      isHeaderOpen,
      rMElementEntry: [page, path],
    });
    if (!pointDetails) return;
    const { affinity, cMPoint: point } = pointDetails;

    AlineTransforms.setSelection(editor, {
      eventSource: SelEventSource.MOUSE,
      props: { affinity, anchor: point, focus: point },
    });

    stopDraggingImage();
    openImageDialogWithImage(image);
  }

  function stopDraggingImage() {
    if (isDraggingImage) {
      setIsDraggingImage(false);
      editorTabStateActions.dragDrop.setMousePageCoords(null);
    }
  }

  const eventHandlers: HTMLAttributes<HTMLDivElement> = {
    onContextMenu: handleContextMenu,
    onDragLeave: stopDraggingImage,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
  };

  return { contextMenuLocation, eventHandlers, handleMouseDown, setContextMenuLocation };
}
